/**
 * Dev-only quick-login persona buttons for the login page.
 * Lets developers skip typing credentials by clicking a persona.
 * Only visible when NODE_ENV !== 'production'.
 *
 * Props:
 *   onSwitching(persona | null) — called when login starts/ends so
 *   the parent can blur the form and show an overlay.
 */

import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { loginWithCredentials } from '../../application/auth/authFlowService';
import { fetchLoginUserSummary } from '../../application/auth/authSessionService';
import {
  writeViewerAccessToken,
  writeViewerRefreshToken,
  writeViewerStoredUser,
  writeViewerStoredUserSummary
} from '../../features/auth/presentation/browserAuthSessionSupport';
import { replaceBrowserRoute } from '../../features/navigation/presentation/browserNavigationSupport';
import { getDashboardPath } from '../../constants/navigation';

const IS_DEV = process.env.NODE_ENV !== 'production';
const LOGIN_URL = '/identity/login/';

const PERSONAS = [
  {
    key: 'admin',
    label: 'Admin',
    email: 'admin@test.octopi.dev',
    bg: 'bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20',
    dot: 'bg-emerald-400'
  },
  {
    key: 'contributor',
    label: 'Contributor',
    email: 'contributor@test.octopi.dev',
    bg: 'bg-sky-500/10 hover:bg-sky-500/20 border-sky-500/20',
    dot: 'bg-sky-400'
  },
  {
    key: 'volunteer',
    label: 'Volunteer',
    email: 'volunteer@test.octopi.dev',
    bg: 'bg-rose-500/10 hover:bg-rose-500/20 border-rose-500/20',
    dot: 'bg-rose-400'
  },
  {
    key: 'sponsor',
    label: 'Sponsor',
    email: 'sponsor@test.octopi.dev',
    bg: 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20',
    dot: 'bg-amber-400'
  },
  {
    key: 'personal',
    label: 'Personal',
    email: 'personal@test.octopi.dev',
    bg: 'bg-violet-500/10 hover:bg-violet-500/20 border-violet-500/20',
    dot: 'bg-violet-400'
  },
  {
    key: 'agentic',
    label: 'Agentic',
    email: 'agentic@test.octopi.dev',
    bg: 'bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/20',
    dot: 'bg-cyan-400'
  }
];

const PASSWORD = 'testpass123';

export default function DevPersonaLogin({ onSwitching }) {
  const [switching, setSwitching] = useState(null);

  const loginAs = useCallback(
    async (persona) => {
      setSwitching(persona.key);
      if (onSwitching) onSwitching(persona);

      try {
        // Use the same login flow as the real login
        const responseData = await loginWithCredentials({
          url: `${LOGIN_URL}?response=minimal`,
          email: persona.email,
          password: PASSWORD
        });

        const tokensPayload =
          responseData?.tokens || responseData?.data?.tokens || {};
        const accessToken =
          tokensPayload.access ||
          responseData?.data?.token ||
          responseData?.token;
        const refreshToken =
          tokensPayload.refresh ||
          responseData?.data?.refresh ||
          responseData?.refresh;

        if (!accessToken) {
          console.error(
            'DevPersonaLogin: no access token in response',
            responseData
          );
          toast.error('Login failed - no token received');
          setSwitching(null);
          if (onSwitching) onSwitching(null);
          return;
        }

        // Store tokens using the same functions as the real login
        writeViewerAccessToken(accessToken);
        if (refreshToken) {
          writeViewerRefreshToken(refreshToken);
        }

        // Build and store user object
        const currentUser = {
          email: responseData?.email,
          username: responseData?.username,
          pk: responseData?.user_id || responseData?.pk || responseData?.id,
          is_onboard_complete: responseData?.is_onboard_complete,
          is_contributor: responseData?.is_contributor,
          active_workspace_id: null,
          active_seed_id: null,
          requires_org_onboarding: responseData?.requires_org_onboarding,
          org_membership_count: responseData?.org_membership_count
        };

        // Fetch user summary to get workspace info
        let resolvedWorkspaceId = null;
        try {
          const summary = await fetchLoginUserSummary();
          if (summary) {
            writeViewerStoredUserSummary(summary);

            resolvedWorkspaceId =
              summary?.workspace_context?.active_workspace_id ||
              summary?.data?.workspace_context?.active_workspace_id ||
              summary?.workspaces?.[0]?.id ||
              summary?.data?.workspaces?.[0]?.id ||
              null;
          }
        } catch (_) {}

        currentUser.active_workspace_id = resolvedWorkspaceId;
        currentUser.active_seed_id = resolvedWorkspaceId;
        writeViewerStoredUser(currentUser);

        // Redirect
        const destination = resolvedWorkspaceId
          ? getDashboardPath(resolvedWorkspaceId)
          : '/home';

        toast.success(`Logged in as ${persona.label}`);
        replaceBrowserRoute(destination);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Login failed';
        console.error('DevPersonaLogin: login failed', errorMessage);
        toast.error(`${persona.label}: ${errorMessage}`);
        setSwitching(null);
        if (onSwitching) onSwitching(null);
      }
    },
    [onSwitching]
  );

  if (!IS_DEV) return null;

  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
      <div className="flex flex-wrap justify-center gap-2">
        {PERSONAS.map((persona) => (
          <button
            key={persona.key}
            type="button"
            disabled={switching !== null}
            onClick={() => loginAs(persona)}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-colors
              ${persona.bg}
              ${
                switching === persona.key
                  ? 'opacity-50 cursor-wait'
                  : 'cursor-pointer text-gray-600 dark:text-gray-300'
              }
              ${
                switching !== null && switching !== persona.key
                  ? 'opacity-30'
                  : ''
              }
            `}
          >
            <span className={`h-2 w-2 rounded-full ${persona.dot}`} />
            <span>
              {switching === persona.key ? 'Logging in...' : persona.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
